import React from 'react';
import {
  Card, CircularGaugeChart, ellipsis, FontWeight, Helper, HStack, Icon, ICON_INFORMATION_OUTLINE, Message, ModalSize, Portal, Spacing, Stack, Text,
  useModal,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import Markdown from 'react-markdown';
import { GoalMetric, ManagerGoal } from '../../../providers/api/dtos';
import { configByMetric, configByPerformanceRating } from './constants';
import { template } from '../../../utils/text/text';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import ManagerGoalPerformanceDetailView from '../manager-goal-performance-detail-view/ManagerGoalPerformanceDetailView';

export interface ManagerGoalPerformanceSummaryCardProps extends React.AriaAttributes {
  metric: GoalMetric,
  goal: ManagerGoal,
}

const ManagerGoalPerformanceSummaryCard: React.FC<ManagerGoalPerformanceSummaryCardProps> = ({
  metric,
  goal,
  ...rest
}) => {
  const detailViewModal = useModal();

  const { yearly } = goal.metrics[metric];
  const cfgByMetric = configByMetric[metric];
  const cfgByPerformanceRating = configByPerformanceRating(goal.metrics.sales.yearly.progress >= 1)[yearly.status];

  const formattedYearlyGoal = Math.round(yearly.goal);

  return (
    <>
      <Card {...rest}>
        <Card.Content>
          <Spacing padding={[50, 0]}>
            <HStack
              gap={100}
              alignItems="start"
              justifyContent="space-between"
              wrap="nowrap"
            >
              <HStack
                gap={300}
                alignItems="start"
                wrap="nowrap"
              >
                <div style={{ width: '6.25rem' }}>
                  <Stack
                    gap={150}
                    alignItems="center"
                  >

                    <CircularGaugeChart
                      progress={yearly.progress}
                      color={cfgByPerformanceRating.variant}
                      size="L"
                    />

                    <Text.Caption
                      color={`${cfgByPerformanceRating.variant}.default.low`}
                      css={ellipsis()}
                    >
                      {cfgByMetric.chartCaption}
                    </Text.Caption>
                  </Stack>
                </div>

                <Spacing padding={[75, 0]}>
                  <Stack gap={150}>
                    <Text.H4 fontWeight={FontWeight.LIGHT}>
                      <Markdown components={{ p: ({ _node, ...props }) => props.children }}>
                        {template(cfgByMetric.titleMarkdown, {
                          PERFORMANCE: yearly.performance,
                          GOAL: formattedYearlyGoal,
                        })}
                      </Markdown>
                    </Text.H4>

                    <Message
                      icon={cfgByPerformanceRating.icon}
                      message={cfgByPerformanceRating.message}
                      type={cfgByPerformanceRating.variant}
                      onClick={() => detailViewModal.open()}
                    />
                  </Stack>
                </Spacing>
              </HStack>

              <Helper
                content={template(cfgByMetric.helperContent, {
                  AVG_COMMISSIONS: formatEuro(goal.averageCommission),
                })}
              >
                <Icon
                  path={ICON_INFORMATION_OUTLINE}
                  size={24}
                />
              </Helper>
            </HStack>
          </Spacing>
        </Card.Content>
      </Card>
      <Portal>
        <SimpleModal
          {...detailViewModal}
          aria-label="Informazioni dettagliate performance obiettivo"
          title={template(cfgByPerformanceRating.modalTitle, {
            METRIC: cfgByMetric.translation,
          })}
          size={ModalSize.MEDIUM}
        >
          <ManagerGoalPerformanceDetailView
            metric={metric}
            goal={goal}
            timeRange={['rumors', 'prospects'].includes(metric) ? 'weekly' : 'monthly'}
            onNewGoalAccepted={() => detailViewModal.close()}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default ManagerGoalPerformanceSummaryCard;
